import { useState } from 'react';
import { deleteConversation, pinConversation, renameConversation } from './conversations';
import { useGenericBi } from './use-generic-bi';

/** The src:172 page descriptor for the conversation lists that live in the app chrome (the rail, the mobile
 *  drawer, the search dialog). They are one surface as far as this vocabulary is concerned — the row menu is
 *  the same control in all three — so they report one page rather than three that can't be compared. */
export const CONVERSATIONS_PAGE = { page_name: 'conversations' as const, page_version: 'v1_conversations' as const };

/** The conversation a confirm is about — id for the write, title for the sentence it shows. */
export interface DeleteConversationTarget {
  id: string;
  title: string;
}

/** The conversation being renamed, plus the title the field started from. */
export interface RenameConversationTarget {
  id: string;
  title: string;
}

export interface ConversationActionsFlow {
  /** The conversation awaiting delete confirmation, or null when no confirm is up. */
  target: DeleteConversationTarget | null;
  busy: boolean;
  /** Set when the delete failed, shown inside the still-open confirm. */
  error: string | null;
  /** Pin/unpin writes straight through — there is nothing to confirm and it is trivially reversible. */
  togglePin: (conv: { id: string; pinned?: boolean }) => Promise<void>;
  requestDelete: (target: DeleteConversationTarget) => void;
  dismissDelete: () => void;
  /** Resolves true once the conversation is gone from the list. */
  confirmDelete: () => Promise<boolean>;
  /** The conversation awaiting a new title, or null when no rename dialog is up. */
  renameTarget: RenameConversationTarget | null;
  requestRename: (target: RenameConversationTarget) => void;
  dismissRename: () => void;
  /** Resolves true once the new title is saved. A blank title is refused without a write. */
  confirmRename: (name: string) => Promise<boolean>;
}

/**
 * The pin and delete actions on a conversation row, shared by every surface that offers them — the wide
 * rail's Chat History, the mobile drawer, and the search dialog — so the confirm step, the BI vocabulary
 * and the optimistic writes cannot drift between them.
 *
 * **Only delete confirms.** A pin is one press to undo and shows its own result (the row moves), so a
 * dialog in front of it is friction with nothing to protect. A delete removes a thread from every surface
 * at once, so it asks first — the same asymmetry the assistant's own tool draws, where `delete` demands
 * `confirmed=true` and `pin` does not.
 *
 * `page` is the src:172 page descriptor of whichever screen the row sits on, so a click reports the screen
 * it happened on while the `button_name` / `componentName` vocabulary stays identical across all of them.
 */
export function useConversationActions(page: { page_name: string; page_version: string }): ConversationActionsFlow {
  const { logPageClickBi } = useGenericBi();
  const [target, setTarget] = useState<DeleteConversationTarget | null>(null);
  const [renameTarget, setRenameTarget] = useState<RenameConversationTarget | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bi = (buttonName: string, conversationId: string) =>
    logPageClickBi(
      { ...page, button_name: buttonName, conversation_id: conversationId },
      { componentName: buttonName.replace(/_/g, '-') },
    );

  return {
    target,
    busy,
    error,
    togglePin: async (conv) => {
      const next = !conv.pinned;
      bi(next ? 'pin_conversation' : 'unpin_conversation', conv.id);
      await pinConversation(conv.id, next);
    },
    renameTarget,
    requestRename: (next) => {
      bi('rename_conversation', next.id);
      setError(null);
      setRenameTarget(next);
    },
    dismissRename: () => {
      setRenameTarget(null);
      setError(null);
    },
    confirmRename: async (name) => {
      const next = name.trim();
      if (!renameTarget || busy || !next) return false;
      bi('confirm_rename_conversation', renameTarget.id);
      setBusy(true);
      setError(null);
      const ok = await renameConversation(renameTarget.id, next);
      setBusy(false);
      if (!ok) {
        // The dialog stays open on failure: closing it would read as a rename that worked.
        setError('failed');
        return false;
      }
      setRenameTarget(null);
      return true;
    },
    requestDelete: (next) => {
      bi('delete_conversation', next.id);
      setError(null);
      setTarget(next);
    },
    dismissDelete: () => {
      setTarget(null);
      setError(null);
    },
    confirmDelete: async () => {
      if (!target || busy) return false;
      bi('confirm_delete_conversation', target.id);
      setBusy(true);
      setError(null);
      const ok = await deleteConversation(target.id);
      setBusy(false);
      if (!ok) {
        // The confirm stays open on failure: closing it would read as a delete that worked.
        setError('failed');
        return false;
      }
      setTarget(null);
      return true;
    },
  };
}
